import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, UsersIcon, UserIcon, SearchIcon, CalendarIcon, TicketIcon, MessageCircleIcon, BriefcaseIcon, MenuIcon, XIcon, FilmIcon, ShoppingCartIcon, SunIcon, MoonIcon, CheckSquareIcon, PercentIcon } from 'lucide-react';
import { useAppStore } from '../store';
const Navigation = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Use global state
  const {
    darkMode,
    toggleDarkMode,
    cartItems
  } = useAppStore();
  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-gray-200 dark:bg-gray-700' : '';
  };
  useEffect(() => {
    // Apply dark mode class to html element
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);
  const navItems = [{
    path: '/',
    icon: <HomeIcon className="w-5 h-5" data-id="element-197" />,
    label: 'Home'
  }, {
    path: '/daily-vibe-check',
    icon: <CheckSquareIcon className="w-5 h-5" data-id="element-198" />,
    label: 'Daily Vibe Check'
  }, {
    path: '/communities',
    icon: <UsersIcon className="w-5 h-5" data-id="element-199" />,
    label: 'Communities'
  }, {
    path: '/jobs',
    icon: <BriefcaseIcon className="w-5 h-5" data-id="element-200" />,
    label: 'Jobs'
  }, {
    path: '/events',
    icon: <CalendarIcon className="w-5 h-5" data-id="element-201" />,
    label: 'Events'
  }, {
    path: '/movies',
    icon: <FilmIcon className="w-5 h-5" data-id="element-202" />,
    label: 'Movies'
  }, {
    path: '/tickets',
    icon: <TicketIcon className="w-5 h-5" data-id="element-203" />,
    label: 'Tickets'
  }, {
    path: '/deals-discounts',
    icon: <PercentIcon className="w-5 h-5" data-id="element-204" />,
    label: 'Deals/Discounts'
  }, {
    path: '/forum',
    icon: <MessageCircleIcon className="w-5 h-5" data-id="element-205" />,
    label: 'Forum'
  }];
  return <header className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-md z-10" data-id="element-206">
      <div className="max-w-6xl mx-auto px-4" data-id="element-207">
        <div className="flex justify-between items-center h-16" data-id="element-208">
          {/* Logo - moved to far left */}
          <Link to="/" className="font-bold text-sm flex items-center transition-transform duration-300 hover:scale-105" data-id="element-209">
            <span className="border border-gray-400 dark:border-gray-500 rounded-md px-2 py-1 bg-gradient-to-r from-green-700 to-red-700 text-white" data-id="element-210">
              JoinToday.co.ke
            </span>
          </Link>
          {/* Desktop Navigation - centered */}
          <nav className="hidden md:flex items-center justify-center flex-1 ml-4" data-id="element-211">
            {navItems.map(item => <Link key={item.path} to={item.path} className={`flex items-center px-2.5 py-2 rounded-md ${isActive(item.path)} hover:bg-gray-100 dark:hover:bg-gray-700 text-sm dark:text-gray-200 whitespace-nowrap transition-all duration-300 hover:scale-105`} data-id="element-212">
                {item.icon}
                <span className="ml-1" data-id="element-213">{item.label}</span>
              </Link>)}
          </nav>
          {/* Right side items */}
          <div className="hidden md:flex items-center space-x-1" data-id="element-214">
            {/* Cart Icon */}
            <Link to="/tickets" className="flex items-center px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 relative dark:text-gray-200 transition-all duration-300 hover:scale-105" data-id="element-215">
              <ShoppingCartIcon className="w-5 h-5" data-id="element-216" />
              {cartItems.length > 0 && <span className="absolute -top-1 -right-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse" data-id="element-217">
                  {cartItems.length}
                </span>}
            </Link>
            <div className="border-l border-gray-300 dark:border-gray-600 h-6 mx-2" data-id="element-218"></div>
            {/* Search - Icon only */}
            <Link to="/search" className={`flex items-center px-3 py-2 rounded-md ${isActive('/search')} hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300 hover:scale-105`} aria-label="Search" data-id="element-219">
              <SearchIcon className="w-5 h-5" data-id="element-220" />
            </Link>
            {/* User Profile */}
            <Link to="/register" className={`flex items-center px-3 py-2 rounded-md ${isActive('/profile')} hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300 hover:scale-105`} data-id="element-221">
              <UserIcon className="w-5 h-5" data-id="element-222" />
              <span className="ml-1" data-id="element-223">Sign Up</span>
            </Link>
            {/* Dark Mode Toggle - Moved to far right */}
            <button onClick={toggleDarkMode} className="flex items-center px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300 hover:scale-105" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-224">
              {darkMode ? <SunIcon className="w-5 h-5" data-id="element-225" /> : <MoonIcon className="w-5 h-5" data-id="element-226" />}
              <span className="ml-1" data-id="element-227">Mode</span>
            </button>
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4" data-id="element-228">
            {/* Cart Icon for Mobile */}
            <Link to="/tickets" className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 relative dark:text-gray-200 transition-all duration-300 hover:scale-105" data-id="element-229">
              <ShoppingCartIcon className="w-6 h-6" data-id="element-230" />
              {cartItems.length > 0 && <span className="absolute -top-1 -right-1 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse" data-id="element-231">
                  {cartItems.length}
                </span>}
            </Link>
            {/* Dark Mode Toggle for Mobile */}
            <button onClick={toggleDarkMode} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300 hover:scale-105" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-232">
              {darkMode ? <SunIcon className="w-6 h-6" data-id="element-233" /> : <MoonIcon className="w-6 h-6" data-id="element-234" />}
            </button>
            <button className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300 hover:scale-105" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-235">
              {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-236" /> : <MenuIcon className="w-6 h-6" data-id="element-237" />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Navigation */}
      {mobileMenuOpen && <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 animate-fadeIn" data-id="element-238">
          <div className="px-2 pt-2 pb-3 space-y-1" data-id="element-239">
            {navItems.map(item => <Link key={item.path} to={item.path} className={`flex items-center px-3 py-2 rounded-md ${isActive(item.path)} hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300`} onClick={() => setMobileMenuOpen(false)} data-id="element-240">
                {item.icon}
                <span className="ml-2" data-id="element-241">{item.label}</span>
              </Link>)}
            <Link to="/search" className={`flex items-center px-3 py-2 rounded-md ${isActive('/search')} hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300`} onClick={() => setMobileMenuOpen(false)} data-id="element-242">
              <SearchIcon className="w-5 h-5 mr-2" data-id="element-243" />
              <span data-id="element-244">Search</span>
            </Link>
            <Link to="/register" className={`flex items-center px-3 py-2 rounded-md ${isActive('/register')} hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 transition-all duration-300`} onClick={() => setMobileMenuOpen(false)} data-id="element-245">
              <UserIcon className="w-5 h-5 mr-2" data-id="element-246" />
              <span data-id="element-247">Sign Up</span>
            </Link>
          </div>
        </div>}
      {/* WhatsApp Contact Button - Updated with Let's Connect text */}
      <a href="https://wa.me/254100254254" target="_blank" rel="noopener noreferrer" className="fixed top-20 right-4 z-50 flex items-center transition-all duration-300 hover:scale-105" aria-label="Contact us on WhatsApp" data-id="element-248">
        <div className="flex items-center" data-id="element-249">
          <div className="bg-red-500 text-white rounded-l-full px-4 py-2 font-medium" data-id="element-250">
            Let's Connect
          </div>
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center" data-id="element-251">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-6 h-6" data-id="element-252">
              <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" data-id="element-253" />
            </svg>
          </div>
        </div>
      </a>
    </header>;
};
export default Navigation;